import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { paymentsAPI } from '../services/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'error'
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await paymentsAPI.getCheckoutStatus(sessionId);
      
      if (response.data.status === 'completed') {
        setStatus('success');
        setPaymentInfo(response.data);
        return;
      }

      // If still processing, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        <div className="bg-card/80 backdrop-blur-3xl border border-white/20 rounded-3xl p-8 shadow-2xl shadow-electric/20" style={{ backdropFilter: 'blur(40px) saturate(180%)' }}>
          
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-electric mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Processing Payment</h1>
              <p className="text-foreground/70">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h1>
              <p className="text-foreground/70 mb-6">
                {paymentInfo?.payment_type === 'subscription' 
                  ? 'Welcome to Pulse Media Premium! You now have unlimited access to all content.'
                  : 'Your purchase was successful! You have 7 days to watch this content.'
                }
              </p>
              
              <div className="space-y-3">
                {paymentInfo?.content_id ? (
                  <button
                    onClick={() => navigate(`/content/${paymentInfo.content_id}`)}
                    className="w-full py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-all"
                    data-testid="watch-now-button"
                  >
                    Watch Now
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/')}
                    className="w-full py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-all"
                    data-testid="browse-button"
                  >
                    Start Watching
                  </button>
                )}
                
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 rounded-xl bg-white/10 text-foreground font-semibold hover:bg-white/20 transition-all"
                >
                  Back to Home
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-destructive mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Payment Issue</h1>
              <p className="text-foreground/70 mb-6">
                We couldn't confirm your payment. If you were charged, please contact support.
              </p>
              
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 rounded-xl bg-white/10 text-foreground font-semibold hover:bg-white/20 transition-all"
              >
                Back to Home
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
